import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { catchError, of } from 'rxjs';
import { Logger, SharedModule } from '@aiwyn/shared';
import { EsignSigningContainerComponent } from '../../../secure-link/containers/esign-signing/esign-signing-container.component';
import { CxpEsignApiService } from '../../services/cxp-esign-api.service';
import { CxpRouteParamsService } from '../../services/cxp-route-params.service';
import { CXP_ROUTE_TOKEN_ESIGN_DETAIL, RECIPIENT_ID_QUERY_PARAM } from '../../models';
import { parseRecipientId } from '../../shared';
import { ROUTE_TOKEN_CXP_TASKS, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';

/**
 * The signing surface as reached from inside the portal.
 *
 * Deliberately thin: the signing flow itself — consent, the Apryse viewer, field
 * locking, submit, and every completion state — is the one the secure link
 * already runs, and a second implementation would be two flows to keep in step
 * on a screen where being out of step means a signature goes astray.
 *
 * All this adds is the bundle. A secure link names its one recipient row on its
 * own; a portal session does not, because the same contact can be a recipient on
 * several bundles, so the id is resolved from the request in the URL and handed
 * down. Resolving it here rather than passing it from the detail page keeps a
 * deep link to this route working on its own.
 */
@Component({
  selector: 'cxp-esign-sign-page',
  templateUrl: './esign-sign-page.component.html',
  styleUrls: ['./esign-sign-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, MatProgressSpinnerModule, EsignSigningContainerComponent],
})
export class EsignSignPageComponent implements OnInit {
  private readonly routeParams = inject(CxpRouteParamsService);
  private readonly route = inject(ActivatedRoute);
  private readonly esignApi = inject(CxpEsignApiService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);

  /** The signature the read resolved — the bundle to sign, and which row to sign it as. */
  protected readonly signature = signal<{ bundleId: number; recipientId: number } | null>(null);

  /**
   * Which signature the URL asks for, when the task list sent the signer here from one
   * of several on the same bundle. Read from the URL rather than passed down so a deep
   * link to a specific signature keeps working.
   *
   * Only the request for a row — the resolved one comes back on the detail and is what the signing
   * flow is given, so a link naming none still signs a definite row rather than an ambiguous one.
   */
  private readonly requestedRecipientId = computed(() =>
    parseRecipientId(this.route.snapshot.queryParamMap.get(RECIPIENT_ID_QUERY_PARAM)),
  );

  /**
   * Return to Portal lands back on the request the signer came from, not the app root. Computed
   * from the route rather than passed down, so a deep link into signing still knows where "back"
   * is; null while the id is missing, which leaves the shared flow on its secure-link behaviour.
   */
  protected readonly returnTo = computed(() => {
    const requestId = this.routeParams.requestId();
    return requestId == null
      ? null
      : [`/${ROUTE_TOKEN_MAIN_BASE}`, ROUTE_TOKEN_CXP_TASKS, CXP_ROUTE_TOKEN_ESIGN_DETAIL, requestId];
  });
  protected readonly isResolving = signal(true);

  ngOnInit(): void {
    const requestId = this.routeParams.requestId();
    if (requestId == null) {
      this.isResolving.set(false);
      return;
    }

    this.esignApi
      .getDetailForClientRequest(requestId, this.requestedRecipientId())
      .pipe(
        catchError((err) => {
          this.logger.error('EsignSignPage :: failed to resolve the bundle to sign', err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        this.signature.set(detail == null ? null : { bundleId: detail.bundleId, recipientId: detail.recipientId });
        this.isResolving.set(false);
      });
  }
}
